import { resolveProviderSelectionPath } from "@src/utils/provider-back-path";

// URL slugs for each step of the patient-information flow.
export const patientInfoRoutes = {
  // Step 1a: insurance eligibility.
  COVER_CARE: "cover-care",
  // Step 1b: policy holder details.
  POLICY_HOLDER: "policy-holder",
  // Step 2: patient's own info.
  YOUR_INFO: "patient-info",
  // Step 3: final review.
  CONFIRM_APPOINTMENT: "confirm-appointment",
} as const;

// Valid slug string literals.
export type PatientInfoSlug = (typeof patientInfoRoutes)[keyof typeof patientInfoRoutes];

// Slug -> step/substep (unknown falls back to step 1).
export const getStepAndSubstepFromSlug = (slug: string): { step: number; substep: number } => {
  switch (slug) {
    case patientInfoRoutes.COVER_CARE:
      return { step: 1, substep: 1 };
    case patientInfoRoutes.POLICY_HOLDER:
      return { step: 1, substep: 2 };
    case patientInfoRoutes.YOUR_INFO:
      return { step: 2, substep: 1 };
    case patientInfoRoutes.CONFIRM_APPOINTMENT:
      return { step: 3, substep: 1 };
    default:
      return { step: 1, substep: 1 };
  }
};

// Step/substep -> slug (unmatched falls back to cover-care).
export const getSlugFromStepAndSubstep = (step: number, substep: number): PatientInfoSlug => {
  if (step === 1 && substep === 1) return patientInfoRoutes.COVER_CARE;
  if (step === 1 && substep === 2) return patientInfoRoutes.POLICY_HOLDER;
  if (step === 2) return patientInfoRoutes.YOUR_INFO;
  if (step === 3) return patientInfoRoutes.CONFIRM_APPOINTMENT;
  return patientInfoRoutes.COVER_CARE;
};

// HTML form IDs for each step, shared between form elements and submit buttons.
export const patientInfoFormIds = {
  COVER_CARE: "cover-care-form",
  POLICY_HOLDER_DETAILS: "policy-holder-details-form",
  YOUR_INFO: "your-info-form",
  CONFIRM_APPT_DETAILS: "confirm-appt-details",
} as const;

// HTML form name for the current step, used by submit buttons.
export const getFormNameForSubmit = (step: number, subStep: number): string | undefined => {
  if (subStep === 1 && step === 1) {
    return patientInfoFormIds.COVER_CARE;
  } else if (subStep === 2 && step === 1) {
    return patientInfoFormIds.POLICY_HOLDER_DETAILS;
  } else if (step === 2) {
    return patientInfoFormIds.YOUR_INFO;
  } else if (step === 3) {
    return patientInfoFormIds.CONFIRM_APPT_DETAILS;
  } else {
    return undefined;
  }
};

// Full URL for a given step/substep.
export const getNavigationPath = (step: number, substep: number): string => {
  const slug = getSlugFromStepAndSubstep(step, substep);
  return `/patient-information/${slug}`;
};

// Where the Back button navigates from the current step/substep.
export const getBackNavigationPath = (
  currentStep: number,
  currentSubstep: number,
  isPrimaryPolicyHolder?: boolean,
  paymentMethod?: string,
  providerBackPath?: string,
): string => {
  if (currentStep === 1 && currentSubstep === 1) {
    // First step: exit to the route the user entered patient-information from.
    return resolveProviderSelectionPath(providerBackPath);
  } else if (currentStep === 1 && currentSubstep === 2) {
    return getNavigationPath(1, 1);
  } else if (currentStep === 2) {
    if (paymentMethod === "self_pay") {
      return resolveProviderSelectionPath(providerBackPath);
    }
    // Check if user filled out policy holder details to determine which substep to go back to
    if (isPrimaryPolicyHolder || isPrimaryPolicyHolder === undefined) {
      return getNavigationPath(1, 1);
    } else {
      return getNavigationPath(1, 2);
    }
  } else {
    // Later steps: go to the previous step.
    return getNavigationPath(currentStep - 1, 1);
  }
};

export const getInitialPatientInformationPath = (paymentMethod?: string): string =>
  `/patient-information/${
    paymentMethod === "self_pay" ? patientInfoRoutes.YOUR_INFO : patientInfoRoutes.COVER_CARE
  }`;

/**
 * Returns the target redirect path if a self_pay user lands on insurance eligibility or policy holder steps.
 */
export const getSelfPayRedirectPath = (slug?: string, paymentMethod?: string): string | null => {
  if (
    paymentMethod === "self_pay" &&
    (slug === patientInfoRoutes.COVER_CARE || slug === patientInfoRoutes.POLICY_HOLDER)
  ) {
    return getNavigationPath(2, 1);
  }
  return null;
};

/**
 * Returns the step tracker step labels and active step index based on payment method.
 */
export const getStepTrackerConfig = (
  currentStep: number,
  infoStepLabel: string,
  paymentMethod?: string,
): { steps: string[]; currentStep: number } => {
  const isSelfPay = paymentMethod === "self_pay";
  const steps = isSelfPay
    ? [infoStepLabel, "Review details"]
    : ["Eligibility", infoStepLabel, "Review details"];
  const activeStep = isSelfPay ? Math.max(1, currentStep - 1) : currentStep;

  return { steps, currentStep: activeStep };
};
